import React, { useState } from "react";
import { Col, Form, Row, Button, Radio, Slider, Input, Tooltip } from "antd";
import { Helmet } from "react-helmet";

import CalculatorBanner from "../../../components/website/CalculatorBanner/CalculatorBanner";
import { CalculatorLinks } from "../../../components/website/CalculatorLinks/CalculatorLinks";
import { CalculatorResult } from "../../../components/website/CalculatorResult/CalculatorResult";
import { CalculatorContent } from "../../../components/website/CalculatorContent/CalculatorContent";

import "./Calculators.css";
import "../../../assets/styles/calculator.css";
import { CheckCircleFilled, CheckCircleOutlined } from "@ant-design/icons";
import bial from "../../../assets/images/BIAL-blue.svg";

/* CALCULADORA CKD-EPI (2021) */
const CalculatorDRC = () => {
  const [minValues] = useState({ age: 18, creatinine: 0, albuminuria: 0 });
  const [maxValues] = useState({ age: 99, creatinine: 4.0, albuminuria: 9999 });
  const [isResultLoading, setIsResultLoading] = useState(false);
  const [errorFinded, setErrorFinded] = useState(null);
  const [resultMessage, setResultMessage] = useState("");
  const [points, setPoints] = useState(null);
  const [formValues, setFormValues] = useState(null);
  const [form] = Form.useForm();

  function handleIncrement(key) {
    let value = form.getFieldValue(key);
    if (typeof value === "string") {
      value = Number(value);
    }
    console.log(value);
    if (value || value === 0) {
      if (value >= minValues[key] && value < maxValues[key]) {
        value++;
      } else if (value < minValues[key]) {
        value = minValues[key];
      } else {
        value = maxValues[key];
      }

      form.setFieldValue([key], Number(value));
      form.validateFields([key]);
    } else {
      form.setFieldValue([key], minValues[key]);
      form.validateFields([key]);
    }
  }

  function handleDecrement(key) {
    let value = form.getFieldValue(key);
    if (typeof value === "string") {
      value = Number(value);
    }

    if (value) {
      if (value > minValues[key] && value <= maxValues[key]) {
        value--;
      } else if (value <= minValues[key]) {
        value = minValues[key];
      } else {
        value = maxValues[key];
      }

      form.setFieldValue([key], Number(value));
      form.validateFields([key]);
    } else {
      form.setFieldValue([key], minValues[key]);
      form.validateFields([key]);
    }
  }

  const handleValuesChange = () => {
    let allValues = form.getFieldsValue();
    let columns = Object.keys(allValues);

    let nullItems = 0;
    let auxErrorsFinded = 0;
    let sumPoints = 0;

    for (let i = 0; i < columns.length; i++) {
      if (typeof allValues[columns[i]] === "string") {
        allValues[columns[i]] = Number(allValues[columns[i]]);
      }

      if (allValues[columns[i]] === null || allValues[columns[i]] === undefined) {
        nullItems++;
      }
    }

    if (nullItems === 0) {
      const validationErrors = Object.values(form.getFieldsError());

      for (let e = 0; e < validationErrors.length; e++) {
        if (validationErrors[e].errors.length > 0) {
          auxErrorsFinded++;
        }
      }
    }

    if (nullItems === 0 && auxErrorsFinded === 0) {
      if (allValues["creatinine"] === 0 || allValues["sex"] == null) {
        setResultMessage("");
      }

      let sex = allValues["sex"];
      let creatinine = allValues["creatinine"];
      let age = allValues["age"];

      let k_val = sex === 1 ? 0.9 : 0.7;
      let a_val = sex === 1 ? -0.302 : -0.241;
      let s_val = sex === 1 ? 1 : 1.012;

      let min_val = 0;
      let max_val = 0;

      if (creatinine / k_val > 1) {
        min_val = 1;
        max_val = creatinine / k_val;
      } else {
        min_val = creatinine / k_val;
        max_val = 1;
      }

      sumPoints = 142 * Math.pow(min_val, a_val) * Math.pow(max_val, -1.2) * Math.pow(0.9938, age) * s_val;

      if (!isNaN(Math.round(sumPoints))) {
        setPoints(Math.round(sumPoints));
        setFormValues(allValues);
        setResultMessage(Math.round(sumPoints) + " ml/min/1.73 m²");
        setIsResultLoading(false);
        setErrorFinded(false);
      }
    } else {
      setPoints(null);
      if (auxErrorsFinded > 0) {
        setErrorFinded(true);
      }
      setResultMessage(null);
    }
  };

  function handleValidate(rule, value) {
    let key = rule.field;
    let keysOfNumber = Object.keys(minValues);
    if (keysOfNumber.includes(key)) {
      if (Number(value) < minValues[rule.field]) {
        return Promise.reject(<Tooltip color="red" placement="bottom" open={true} title={`Este valor não pode ser menor que ${minValues[rule.field]}`}></Tooltip>); // The validator should always return a promise on both success and error
      } else if (Number(value) > maxValues[rule.field]) {
        return Promise.reject(<Tooltip color="red" placement="bottom" open={true} title={`Este valor não pode ser maior que ${maxValues[rule.field]}`}></Tooltip>); // The validator should always return a promise on both success and error
      } else {
        return Promise.resolve(); // The validator should always return a promise on both success and error
      }
    }
  }

  return (
    <>
      <Helmet>
        <title>Calculadora Estratificação e Proteção Renal na Doença Renal Crónica | Formação Científica em Medicina Cardiovascular</title>
        <meta
          name="description"
          content="O objetivo Cardio4all é trazer formação científica de alta qualidade, que seja eminentemente prática clínica e interativa, para ajudar os clínicos a melhorarem o diagnóstico e tratamento das doenças cardiovasculares."
        />
        <meta
          name="keywords"
          content="calculadoras área cardiovascular, Cardio4all, Medicina Cardiovascular, formação científica médica, prática clínica cardiologia, doenças cardiovasculares, formação médica em Medicina Geral e Familiar e de Medicina Interna, diagnóstico e tratamento da doença cardiovascular"
        />
        <meta property="og:title" content="Calculadora DRC | Formação Científica em Medicina Cardiovascular" />
        <meta
          property="og:description"
          content="O objetivo Cardio4all é trazer formação científica de alta qualidade, que seja eminentemente prática clínica e interativa, para ajudar os clínicos a melhorarem o diagnóstico e tratamento das doenças cardiovasculares."
        />
      </Helmet>
      <CalculatorBanner title="ESTRATIFICAÇÃO E PROTEÇÃO RENAL NA DOENÇA RENAL CRÓNICA" />

      <CalculatorContent className="mt-10">
        <Form className="form_calculator" form={form} onFieldsChange={handleValuesChange}>
          {/* Row #1 */}
          <Row
            gutter={[
              { xs: 20, sm: 8, md: 16, lg: 60 },
              { xs: 40, sm: 8, md: 16, lg: 60 },
            ]}
          >
            <Col xs={24} sm={12} md={12} lg={12} xl={8}>
              <p className="blue bold">Creatinina sérica (mg/dL)</p>
              <Row className="mt-10 d-flex flex-nowrap">
                <Form.Item name={"creatinine"} className="w-100 mb-0">
                  <Slider min={0.3} max={4.0} step={0.01} onChange={() => form.validateFields()} />
                </Form.Item>
              </Row>
              <Row className="jc-sb ai-center flex-nowrap">
                <span style={{ color: "#C3D0DA" }}>0.3</span>
                <Form.Item
                  className="w-100 d-flex jc-center ai-center mb-0 creatinine-form-item"
                  shouldUpdate={(prevValues, currentValues) => prevValues.creatinine !== currentValues.creatinine}
                  name={"creatinine"}
                  rules={[{ validator: handleValidate }]}
                >
                  <Input type="number" className="creatinine p-0" />
                </Form.Item>
                <span style={{ color: "#C3D0DA" }}>4.0</span>
              </Row>
            </Col>
            <Col xs={24} sm={12} md={12} lg={12} xl={8}>
              <p className="blue bold">Sexo</p>
              <Form.Item name={"sex"} className="mt-10 w-100 mb-0">
                <Radio.Group className="w-100 d-flex flex-nowrap">
                  <Radio.Button value={0} className="w-100 mr-10">
                    Feminino
                  </Radio.Button>
                  <Radio.Button value={1} className="w-100 ml-10">
                    Masculino
                  </Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={12} lg={12} xl={8}>
              <p className="blue bold">Idade</p>
              <Row className="mt-10 d-flex flex-nowrap">
                <Button onClick={() => handleDecrement("age")} className="calculator_button mr-10">
                  -
                </Button>
                <Form.Item name={"age"} className="w-100 mb-0" rules={[{ validator: handleValidate }]}>
                  <Input type="number" className="w-100" />
                </Form.Item>
                <Button onClick={() => handleIncrement("age")} className="calculator_button ml-10">
                  +
                </Button>
              </Row>
              <p className="f-14 mt-10">Valor entre: 18 - 99</p>
            </Col>
            <Col xs={24} sm={12} md={12} lg={12} xl={8}>
              <p className="blue bold">Albuminúria (mg/g)</p>
              <Row className="mt-10 d-flex flex-nowrap">
                <Button onClick={() => handleDecrement("albuminuria")} className="calculator_button mr-10">
                  -
                </Button>
                <Form.Item name={"albuminuria"} className="w-100 mb-0" rules={[{ validator: handleValidate }]}>
                  <Input type="number" className="w-100" />
                </Form.Item>
                <Button onClick={() => handleIncrement("albuminuria")} className="calculator_button ml-10">
                  +
                </Button>
              </Row>
            </Col>
            <Col xs={24} sm={12} md={12} lg={12} xl={8}>
              <p className="blue bold">É diabético?</p>
              <Form.Item name={"diabetic"} className="mt-10 w-100 mb-0">
                <Radio.Group className="w-100 d-flex flex-nowrap">
                  <Radio.Button value={0} className="w-100 mr-10">
                    Não
                  </Radio.Button>
                  <Radio.Button value={1} className="w-100 ml-10">
                    Sim
                  </Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </CalculatorContent>

      {resultMessage && (
        <>
          <CalculatorResult isLoading={isResultLoading}>
            <Row className="ai-center jc-start">
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Row gutter={[24]}>
                  <Col xs={24} sm={12} md={8} lg={4} xl={5}>
                    <p>Albuminúria</p>
                    <h3 className="f-30 bold">{formValues.albuminuria}</h3>
                  </Col>
                  <Col xs={24} sm={20} md={20} lg={20} xl={19}>
                    <Row className="d-flex jc-center ai-center" style={{ background: "#e5e5e6", borderRadius: 5, height: "100%" }}>
                      <Col xs={24} sm={8} md={8} lg={4} xl={4} className="p-10 d-flex jc-center ai-center" style={{ borderRight: "1px solid #000" }}>
                        <p className="bold" style={{ color: "#d50032" }}>
                          {formValues.albuminuria < 30 ? "A1" : formValues.albuminuria >= 30 && formValues.albuminuria <= 300 ? "A2" : "A3"}
                        </p>
                      </Col>
                      <Col xs={24} sm={16} md={16} lg={20} xl={20} className="p-10">
                        <p style={{ color: "#000" }}>
                          {formValues.albuminuria < 30
                            ? "Normal a ligeiramente aumentada"
                            : formValues.albuminuria >= 30 && formValues.albuminuria <= 300
                            ? "Moderadamente aumentada"
                            : "Gravemente aumentada"}
                        </p>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Row gutter={[24]}>
                  <Col xs={24} sm={12} md={8} lg={4} xl={5}>
                    <p>TFG</p>
                    <h3 className="f-30 bold">{points}</h3>
                  </Col>
                  <Col xs={24} sm={20} md={20} lg={20} xl={19}>
                    <Row className="d-flex jc-center ai-center" style={{ background: "#e5e5e6", borderRadius: 5, height: "100%" }}>
                      <Col xs={24} sm={8} md={8} lg={4} xl={4} className="p-10 d-flex jc-center ai-center" style={{ borderRight: "1px solid #000" }}>
                        <p className="bold" style={{ color: "#d50032" }}>
                          {points >= 90
                            ? "G1"
                            : points >= 60 && points <= 89
                            ? "G2"
                            : points >= 45 && points <= 59
                            ? "G3a"
                            : points >= 30 && points <= 44
                            ? "G3a"
                            : points >= 15 && points <= 29
                            ? "G4"
                            : "G5"}
                        </p>
                      </Col>
                      <Col xs={24} sm={16} md={16} lg={20} xl={20} className="p-10">
                        <p style={{ color: "#000" }}>
                          {points >= 90
                            ? "Normal ou elevada"
                            : points >= 60 && points <= 89
                            ? "Ligeiramente diminuída"
                            : points >= 45 && points <= 59
                            ? "Ligeira a moderadamente diminuída"
                            : points >= 30 && points <= 44
                            ? "Moderada a gravemente diminuída"
                            : points >= 15 && points <= 29
                            ? "Gravemente diminuída"
                            : "Falência renal"}
                        </p>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </CalculatorResult>
          <Row className="ai-start jc-start container" gutter={[24]}>
            <Col xs={24} sm={24} md={12} lg={10} xl={10} className="pl-0">
              <table className="text-center table-drc w-100" style={{ borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th colSpan="3" rowSpan="3" style={{ borderTopLeftRadius: 6 }}></th>
                    <th colSpan="3" style={{ borderTopRightRadius: 6 }}>
                      Grau de albuminúria
                    </th>
                  </tr>
                  <tr>
                    <th>A1</th>
                    <th>A2</th>
                    <th>A3</th>
                  </tr>
                  <tr>
                    <th className="f-11">&lt;30 mg/g</th>
                    <th className="f-11">30-300 mg/g</th>
                    <th className="f-11">&gt;300 mg/g</th>
                  </tr>
                </thead>
                <tbody>
                  {/* G1 */}
                  <tr>
                    <th rowSpan="6" style={{ borderBottomLeftRadius: 6 }}>
                      <p className="horizontal-text">Categoria de TFG (ml/min/1.73m²)</p>
                    </th>
                    <td>G1</td>
                    <td>≥90</td>
                    <td style={{ background: "#198754", borderColor: "#FFF" }}>
                      {points >= 90 && formValues.albuminuria < 30 ? <CheckCircleFilled className="white f-18" /> : null}
                    </td>
                    <td style={{ background: "#ffc107", borderColor: "#FFF" }}>
                      {points >= 90 && formValues.albuminuria >= 30 && formValues.albuminuria <= 300 ? <CheckCircleFilled className="white f-18" /> : null}
                    </td>
                    <td style={{ background: "#fd7e14", borderLeftColor: "#FFF", borderBottomColor: "#FFF", borderRightColor: "grey" }}>
                      {points >= 90 && formValues.albuminuria > 300 ? <CheckCircleFilled className="white f-18" /> : null}
                    </td>
                  </tr>

                  {/* G2 */}
                  <tr>
                    <td>G2</td>
                    <td>60-89</td>
                    <td style={{ background: "#198754", borderColor: "#FFF" }}>
                      {points >= 60 && points <= 89 && formValues.albuminuria < 30 ? <CheckCircleFilled className="white f-18" /> : null}
                    </td>
                    <td style={{ background: "#ffc107", borderColor: "#FFF" }}>
                      {points >= 60 && points <= 89 && formValues.albuminuria >= 30 && formValues.albuminuria <= 300 ? <CheckCircleFilled className="white f-18" /> : null}
                    </td>
                    <td style={{ background: "#fd7e14", borderLeftColor: "#FFF", borderBottomColor: "#FFF", borderRightColor: "grey" }}>
                      {points >= 60 && points <= 89 && formValues.albuminuria > 300 ? <CheckCircleFilled className="white f-18" /> : null}
                    </td>
                  </tr>

                  {/* G3a */}
                  <tr>
                    <td>G3a</td>
                    <td className="border border-gray-300 p-2">45-59</td>
                    <td style={{ background: "#ffc107", borderColor: "#FFF" }}>
                      {points >= 45 && points <= 59 && formValues.albuminuria < 30 ? <CheckCircleFilled className="white f-18" /> : null}
                    </td>
                    <td style={{ background: "#fd7e14", borderColor: "#FFF" }}>
                      {points >= 45 && points <= 59 && formValues.albuminuria >= 30 && formValues.albuminuria <= 300 ? <CheckCircleFilled className="white f-18" /> : null}
                    </td>
                    <td style={{ background: "#dc3545", borderLeftColor: "#FFF", borderBottomColor: "#FFF", borderRightColor: "grey" }}>
                      {points >= 45 && points <= 59 && formValues.albuminuria > 300 ? <CheckCircleFilled className="white f-18" /> : null}
                    </td>
                  </tr>

                  {/* G3b */}
                  <tr>
                    <td>G3b</td>
                    <td>30-44</td>
                    <td style={{ background: "#fd7e14", borderColor: "#FFF" }}>
                      {points >= 30 && points <= 44 && formValues.albuminuria < 30 ? <CheckCircleFilled className="white f-18" /> : null}
                    </td>
                    <td style={{ background: "#dc3545", borderColor: "#FFF" }}>
                      {points >= 30 && points <= 44 && formValues.albuminuria >= 30 && formValues.albuminuria <= 300 ? <CheckCircleFilled className="white f-18" /> : null}
                    </td>
                    <td style={{ background: "#dc3545", borderLeftColor: "#FFF", borderBottomColor: "#FFF", borderRightColor: "grey" }}>
                      {points >= 30 && points <= 44 && formValues.albuminuria > 300 ? <CheckCircleFilled className="white f-18" /> : null}
                    </td>
                  </tr>

                  {/* G4 */}
                  <tr>
                    <td>G4</td>
                    <td>15-29</td>
                    <td style={{ background: "#dc3545", borderColor: "#FFF" }}>
                      {points >= 15 && points <= 29 && formValues.albuminuria < 30 ? <CheckCircleFilled className="white f-18" /> : null}
                    </td>
                    <td style={{ background: "#dc3545", borderColor: "#FFF" }}>
                      {points >= 15 && points <= 29 && formValues.albuminuria >= 30 && formValues.albuminuria <= 300 ? <CheckCircleFilled className="white f-18" /> : null}
                    </td>
                    <td style={{ background: "#dc3545", borderTopColor: "#FFF", borderBottomColor: "#FFF", borderLeftColor: "#FFF" }}>
                      {points >= 15 && points <= 29 && formValues.albuminuria > 300 ? <CheckCircleFilled className="white f-18" /> : null}
                    </td>
                  </tr>

                  {/* G5 */}
                  <tr style={{ borderBottomRightRadius: 6 }}>
                    <td>G5</td>
                    <td>&lt;15</td>
                    <td style={{ background: "#dc3545", borderColor: "#FFF", borderBottomColor: "grey" }}>
                      {points < 15 && formValues.albuminuria < 30 ? <CheckCircleFilled className="white f-18" /> : null}
                    </td>
                    <td style={{ background: "#dc3545", borderColor: "#FFF", borderBottomColor: "grey" }}>
                      {points < 15 && formValues.albuminuria >= 30 && formValues.albuminuria <= 300 ? <CheckCircleFilled className="white f-18" /> : null}
                    </td>
                    <td style={{ background: "#dc3545", borderTopColor: "#FFF", borderLeftColor: "#FFF", borderBottomColor: "grey" }}>
                      {points < 15 && formValues.albuminuria > 300 ? <CheckCircleFilled className="white f-18" /> : null}
                    </td>
                  </tr>
                </tbody>
              </table>
            </Col>
            <Col xs={24} sm={24} md={12} lg={14} xl={14} className="pr-0">
              <p className="bold">Recomendações farmacológicas para nefroproteção</p>
              <Row className="mt-10">
                <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                  <div style={{ background: "rgb(191, 143, 0)", borderRadius: 6 }} className="p-10">
                    <p className="bold white">Inibidores do sistema renina-angiotensina (IECA ou ARA)</p>
                    {formValues.diabetic === 1 ? (
                      <p className="mt-5 white">
                        {formValues.albuminuria >= 30 ? "Recomendado" : "Sem recomendação"} {formValues.albuminuria >= 30 ? "na dose máxima tolerada</p>" : null}
                      </p>
                    ) : (
                      <>
                        <p className="mt-5 white">
                          {formValues.albuminuria >= 30 && formValues.albuminuria <= 300 ? "Sugerido" : formValues.albuminuria > 300 ? "Recomendado" : "Sem recomendação"}
                          {(formValues.albuminuria >= 30 && formValues.albuminuria <= 300) || formValues.albuminuria > 300 ? "na dose máxima tolerada" : null}
                        </p>
                      </>
                    )}
                  </div>
                </Col>
                <Col xs={24} sm={24} md={24} lg={24} xl={24} className="mt-10">
                  <div style={{ background: "rgb(48, 84, 150)", borderRadius: 6 }} className="p-10">
                    <p className="bold white">Inibidores SGLT2</p>
                    {formValues.diabetic === 1 ? (
                      <>
                        <p className="mt-5 white">{points >= 20 ? "Recomendado" : "Sem recomendação"}</p>
                        {points >= 20 ? <p className="mt-5 white">(ex: empagliflozina 10mg ou dapagliflozina 10mg)</p> : null}
                      </>
                    ) : (
                      <>
                        <p className="mt-5 white">
                          {points >= 20 && formValues.albuminuria >= 200
                            ? "Recomendado"
                            : points >= 20 && points <= 45 && formValues.albuminuria < 200
                            ? "Sugerido"
                            : "Sem recomendação"}
                        </p>
                        {points >= 20 && formValues.albuminuria >= 200 ? <p className="mt-5 white">(ex: empagliflozina 10mg ou dapagliflozina 10mg)</p> : null}
                      </>
                    )}
                  </div>
                </Col>

                <Col xs={24} sm={24} md={24} lg={24} xl={24} className="mt-10">
                  {formValues.diabetic === 1 ? (
                    <div style={{ background: "rgb(84, 130, 53)", borderRadius: 6 }} className="p-10">
                      <p className="bold white">Finerenona</p>
                      <p className="mt-5 white">{points >= 25 && formValues.albuminuria > 30 ? "Sugere-se" : "Sem recomendação"}</p>
                      {points >= 25 && formValues.albuminuria > 30 ? (
                        <p className="f-11 white">(em doente já medicado com iSRAA na dose máxima tolerada, com normocalémia)</p>
                      ) : null}
                      {points >= 60 && formValues.albuminuria > 30 ? <p className="mt-5 white">Sugere-se dose inicial 20mg</p> : null}
                      {points >= 25 && points < 60 && formValues.albuminuria > 30 ? <p className="mt-5 white">Sugere-se dose inicial 10mg</p> : null}
                    </div>
                  ) : (
                    <div style={{ background: "rgb(84, 130, 53)", borderRadius: 6 }} className="p-10">
                      <p className="bold white">Finerenona</p>
                      <p className="mt-5 white">Sem recomendação</p>
                    </div>
                  )}
                </Col>
              </Row>

              <Row className="mt-10" gutter={[24]}>
                <Col xs={24} sm={24} md={12} lg={12} xl={12} className="mt-10">
                  <p className="bold mt-10 bold f-14">Recomendações iSRAA</p>
                  <p className="f-12 mt-5">
                    Devem ser usados na dose mais alta aprovada de acordo com a tolerância. As alterações na pressão arterial, creatinina sérica e potássio sérico devem ser
                    avaliadas em 2-4 semanas após início ou titulação de iSRAA, sendo aceitáveis elevações até 30% da creatinina sérica em 4 semanas.
                  </p>
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12} className="mt-10">
                  <p className="mt-10 bold f-14">Recomendações iSGLT2</p>
                  <p className="f-12 mt-5">Após o seu início, pode-se considerar continuar a terapêutica mesmo que a TFGe progrida para &lt;20mL/min</p>
                  <p className="f-12 mt-5">Não é necessário alterar a frequência da monitorização da função renal com o início da terapêutica.</p>
                  <p className="f-12 mt-5">Diminuições reversíveis da TFGe após o seu início não são uma indicação para descontinuar o iSGLT2.</p>
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12} className="mt-10">
                  <p className="mt-10 bold f-14">Recomendações finerenona</p>
                  <p className="f-12 mt-5">Não iniciar se potássio sérico &gt;5.0</p>
                  <p className="f-12 mt-5">O potássio sérico e a TFGe devem ser reavaliados 4 semanas após o seu início.</p>
                  <p className="f-12 mt-5">
                    Se iniciada finerenona 10mg e K≤4.8, pode ser aumentada para 20mg (apenas se não houver decréscimo TFGe&gt;30%); se 4.8&lt;K≤5.5, manter 10mg; se K&gt;5.5,
                    suspender.
                  </p>
                  <p className="f-12 mt-5">Se iniciada finerenona 20mg e K≤5.5, manter 20mg; se K&gt;5.5, suspender.</p>
                </Col>
              </Row>
            </Col>
          </Row>
        </>
      )}

      {errorFinded && (
        <CalculatorResult isLoading={isResultLoading} className="drc">
          <Row gutter={[24, 24]} className="h-100 ai-center jc-center">
            <Col xs={24} sm={24} md={16} lg={16} xl={16} className="d-flex jc-center ai-center">
              <p className="f-20 text-center">Algum valor não está entre os parâmetros requeridos</p>
            </Col>
          </Row>
        </CalculatorResult>
      )}

      <div className="container d-flex jc-end w-100">
        <div className="d-flex flex-column jc-end ai-end" style={{ maxWidth: 400 }}>
          <p className="blue f-16 text-right">Esta calculadora foi desenvolvida com o patrocínio independente de:</p>
          <img src={bial} className="w-100 maxw-200 mt-20" />
        </div>
      </div>

      <CalculatorLinks />
    </>
  );
};
export default CalculatorDRC;
